import * as XLSX from 'xlsx';
import Papa from 'papaparse';

export interface ParsedFileData {
  data: any[][];
  columns: string[];
  fileName: string;
  sheets?: string[]; // Available sheet names for Excel files
  selectedSheet?: string; // Currently selected sheet name
}

/**
 * Parse Excel (.xlsx) or CSV files and return structured data
 * @param file - The file to parse
 * @param sheetName - Optional sheet name for Excel files (defaults to first sheet)
 * @returns Promise<ParsedFileData>
 */
export const parseFile = async (file: File, sheetName?: string): Promise<ParsedFileData> => {
  const fileName = file.name;
  const fileType = file.type;

  if (fileType === 'text/csv') {
    return parseCsvFile(file, fileName);
  } else if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
             fileType === 'application/vnd.ms-excel') {
    return parseExcelFile(file, fileName, sheetName);
  } else if (fileType === 'application/pdf') {
    // For PDF files, we can't parse locally - return empty structure
    // The API will handle the PDF processing
    return {
      data: [],
      columns: [],
      fileName
    };
  } else {
    throw new Error(`Unsupported file type: ${fileType}`);
  }
};

/**
 * Get all sheet names from an Excel file
 * @param file - The Excel file
 * @returns Promise<string[]>
 */
export const getExcelSheetNames = async (file: File): Promise<string[]> => {
  if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
      file.type !== 'application/vnd.ms-excel') {
    throw new Error('File is not an Excel file');
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        resolve(workbook.SheetNames);
      } catch (error) {
        reject(new Error(`Error reading Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Parse CSV file using PapaParse
 */
const parseCsvFile = async (file: File, fileName: string): Promise<ParsedFileData> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      complete: (results) => {
        try {
          const data = results.data as string[][];

          // Filter out empty rows
          const nonEmptyData = data.filter(row => 
            row.some(cell => cell && cell.toString().trim() !== '')
          );

          if (nonEmptyData.length === 0) {
            reject(new Error('CSV file appears to be empty'));
            return;
          }

          // Generate column headers (A, B, C, etc.)
          const maxColumns = Math.max(...nonEmptyData.map(row => row.length));
          const columns = Array.from({ length: maxColumns }, (_, i) => 
            String.fromCharCode(65 + i) // A, B, C, etc.
          );

          // Ensure all rows have the same number of columns
          const normalizedData = nonEmptyData.map(row => {
            const normalizedRow = [...row];
            while (normalizedRow.length < maxColumns) {
              normalizedRow.push('');
            }
            return normalizedRow.map(cell => cell || '[EMPTY]');
          });

          resolve({
            data: normalizedData,
            columns,
            fileName
          });
        } catch (error) {
          reject(new Error(`Error parsing CSV: ${error instanceof Error ? error.message : 'Unknown error'}`));
        }
      },
      error: (error) => {
        reject(new Error(`Papa Parse error: ${error.message}`));
      },
      skipEmptyLines: false,
      header: false
    });
  });
};

/**
 * Parse Excel file using SheetJS
 */
const parseExcelFile = async (file: File, fileName: string, sheetName?: string): Promise<ParsedFileData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get the specified worksheet or the first one
        const selectedSheetName = sheetName && workbook.SheetNames.includes(sheetName) 
          ? sheetName 
          : workbook.SheetNames[0];

        if (!selectedSheetName) {
          reject(new Error('Excel file appears to have no worksheets'));
          return;
        }

        const worksheet = workbook.Sheets[selectedSheetName];

        // Convert to array of arrays
        const rawData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1,
          defval: '',
          raw: false // Convert everything to strings
        }) as any[][];

        // Handle completely empty worksheets
        if (rawData.length === 0) {
          console.warn(`Excel worksheet "${selectedSheetName}" is empty, returning minimal structure`);
          resolve({
            data: [['[EMPTY]']],
            columns: ['A'],
            fileName,
            sheets: workbook.SheetNames,
            selectedSheet: selectedSheetName
          });
          return;
        }

        // Filter out completely empty rows
        const nonEmptyData = rawData.filter(row => 
          row.some(cell => cell && cell.toString().trim() !== '')
        );

        // Handle worksheets with only empty rows
        if (nonEmptyData.length === 0) {
          console.warn(`Excel worksheet "${selectedSheetName}" contains only empty rows, returning minimal structure`);
          resolve({
            data: [['[EMPTY]']],
            columns: ['A'],
            fileName,
            sheets: workbook.SheetNames,
            selectedSheet: selectedSheetName
          });
          return;
        }

        // Generate column headers (A, B, C, etc.)
        const maxColumns = Math.max(...nonEmptyData.map(row => row.length), 1); // Ensure at least 1 column
        const columns = Array.from({ length: maxColumns }, (_, i) => 
          String.fromCharCode(65 + i) // A, B, C, etc.
        );

        // Ensure all rows have the same number of columns
        const normalizedData = nonEmptyData.map(row => {
          const normalizedRow = [...row];
          while (normalizedRow.length < maxColumns) {
            normalizedRow.push('');
          }
          return normalizedRow.map(cell => 
            cell === null || cell === undefined || cell === '' ? '[EMPTY]' : String(cell)
          );
        });

        resolve({
          data: normalizedData,
          columns,
          fileName,
          sheets: workbook.SheetNames,
          selectedSheet: selectedSheetName
        });
      } catch (error) {
        reject(new Error(`Error parsing Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };

    reader.readAsArrayBuffer(file);
  });
};